import { resetSegmentIdentity } from '@mc/track-event'
import { clearPaymentUpdatedNotice } from '../components/PaymentUpdatedToast'
import { getBasePath } from '../config/basePath'
import { paths } from './paths'
import { redirectWithBasePath } from './redirect'

const CORE_SIGN_OUT_TIMEOUT_MS = 5000

/**
 * Sign the browser out of masterclass.com. Resolves false when Core could not
 * be reached in time or refused. Same-origin on purpose: Executive and
 * masterclass.com share a host, and Core does not allow credentialed CORS.
 */
export const signOutOfCore = async (): Promise<boolean> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), CORE_SIGN_OUT_TIMEOUT_MS)
  try {
    const csrfResponse = await fetch('/api/v2/csrf-token', {
      credentials: 'same-origin',
      signal: controller.signal,
    })
    if (!csrfResponse.ok) {
      return false
    }
    const { token } = await csrfResponse.json()
    const response = await fetch('/sign_out', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': token },
      signal: controller.signal,
    })
    return response.ok
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

const postLogout = async ({ includeCore }: { includeCore: boolean }) => {
  try {
    const basePath = getBasePath()
    const response = await fetch(`${basePath}/api/auth/logout`, {
      method: 'POST',
    })

    if (!response.ok) {
      return false
    }

    const body = (await response.json()) as { signOutOfCore?: boolean }
    if (includeCore && body.signOutOfCore) {
      await signOutOfCore()
    }

    return true
  } catch {
    // Swallow network errors – we'll still redirect the user.
    return false
  }
}

export const logout = async (returnTo?: string) => {
  resetSegmentIdentity()
  clearPaymentUpdatedNotice()
  const didLogout = await postLogout({ includeCore: true })
  // Only honor custom destinations when the logout request succeeds.
  redirectWithBasePath(didLogout && returnTo ? returnTo : paths.root)
}

// Automatic sign-outs keep the masterclass.com session, which "Continue as" offers.
export const forceLogout = async (returnTo?: string) => {
  resetSegmentIdentity()
  clearPaymentUpdatedNotice()
  const didLogout = await postLogout({ includeCore: false })
  redirectWithBasePath(didLogout && returnTo ? returnTo : paths.login)
}
